import { toBase64 } from '../../../shared/util/base64';
import convertPromiseToQueryReturnValue from '../../shared/convertPromiseToQueryReturnValue';
import { DeviceDataAPIResponse } from '../device.model';
import { DeviceAPI } from '../DeviceAPI';
import { DeviceStatusAdapter } from './adapter/DeviceStatusAdapter';
import { CommanderAPIService } from './CommanderAPIService';
import {
  DeviceStatusAirConnectAPIResponse,
  DeviceStatusAirConnectSummaryAPIResponse,
  DeviceStatusMapAPIResponse,
  DeviceStatusSummaryAPIResponse,
} from './deviceStatus.model';

import Environment from '../../../shared/constants/Environment';
import { uniq } from 'lodash';

const backendDevice = Environment.backendAPICommander;
const backendConfig = Environment.backendAPIConfig;
const backendStatus = Environment.backendAPIStatus;

export const CommanderAPI = DeviceAPI.injectEndpoints({
  endpoints: (build) => ({
    getDeviceByCloudConnectId: build.query<
      DeviceDataAPIResponse | undefined,
      string
    >({
      query: (cloudConnectId) => {
        return {
          url: `${backendDevice}/cloudConnectID/${cloudConnectId}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    getDeviceBySerialNumber: build.query<
      DeviceDataAPIResponse | undefined,
      string
    >({
      query: (serialNumber) => {
        return {
          url: `${backendDevice}/serialnum/${toBase64(serialNumber)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    getDeviceById: build.query<DeviceDataAPIResponse | undefined, string>({
      query: (id) => {
        return {
          url: `${backendDevice}/${toBase64(id)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    setTimezoneCommander: build.mutation<
      string,
      { commanderId: string; timezone: string }
    >({
      query: ({ commanderId, timezone }) => {
        const encodedCommanderId = toBase64(commanderId);
        return {
          url: `${backendConfig}/${encodedCommanderId}`,
          method: 'put',
          data: [
            {
              key: 'device-timezone',
              value: timezone,
              type: 'TEXT',
            },
          ],
        };
      },
      invalidatesTags: ['device-actions'],
    }),
    getDevicesStatus: build.query({
      queryFn: (commanderIds: string[]) => {
        const uniqueCommanderIds = uniq(commanderIds);
        const response = new CommanderAPIService()
          // .getDevicesStatus(commanderIds)
          .getDevicesConnectionStatus(uniqueCommanderIds)
          .then((data) =>
            DeviceStatusAdapter.setAll(
              DeviceStatusAdapter.getInitialState(),
              data
            )
          );
        return convertPromiseToQueryReturnValue(response);
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/workspace/:encodedWorkspaceID
    getDevicesConnectionStatusByWorkspace: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `${backendStatus}/connection/workspace/${toBase64(workspaceId)}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/workspace/:encodedWorkspaceID/count
    getDevicesConnectionStatusCountByWorkspace: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `${backendStatus}/connection/workspace/${toBase64(
            workspaceId
          )}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/group/:encodedGroupID
    getDevicesConnectionStatusByGroup: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `${backendStatus}/connection/group/${toBase64(groupId)}`,
          method: 'get',
        };
      },
      providesTags: (_result, _error, groupId) => [
        'device-actions',
        { type: 'group-actions', id: groupId },
      ],
    }),
    // api/devicestate/connection/group/:encodedGroupID/count
    getDevicesConnectionStatusCountByGroup: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `${backendStatus}/connection/group/${toBase64(groupId)}/count`,
          method: 'get',
        };
      },
      providesTags: (_result, _error, groupId) => [
        'device-actions',
        { type: 'group-actions', id: groupId },
      ],
    }),
    // api/devicestate/connection/user
    getDevicesConnectionStatusByUser: build.query<
      DeviceStatusMapAPIResponse[] | undefined,
      void
    >({
      query: () => {
        return {
          url: `${backendStatus}/connection/user`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // api/devicestate/connection/user/count
    getDevicesConnectionStatusCountByUser: build.query<
      DeviceStatusSummaryAPIResponse | undefined,
      void
    >({
      query: () => {
        return {
          url: `${backendStatus}/connection/user/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    // GET {{BaseURL}}/api/commander/airconnect/status/user/count
    getAirConnectStatusCountByUser: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      void
    >({
      query: () => {
        return {
          url: `${backendDevice}/airconnect/status/user/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/group/:encodedGroupID/count
    getAirConnectStatusCountByGroup: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `${backendDevice}/airconnect/status/group/${toBase64(
            groupId
          )}/count`,
          method: 'get',
        };
      },
      providesTags: (_result, _error, groupId) => [
        'device-actions',
        { type: 'group-actions', id: groupId },
      ],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/workspace/:encodedWorkspaceID/count
    getAirConnectStatusCountByWorkspace: build.query<
      DeviceStatusAirConnectSummaryAPIResponse | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `${backendDevice}/airconnect/status/workspace/${toBase64(
            workspaceId
          )}/count`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/user
    getAirConnectStatusByUser: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      void
    >({
      query: () => {
        return {
          url: `${backendDevice}/airconnect/status/user`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/group/:encodedGroupID
    getAirConnectStatusByGroup: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      string
    >({
      query: (groupId: string) => {
        return {
          url: `${backendDevice}/airconnect/status/group/${toBase64(groupId)}`,
          method: 'get',
        };
      },
      providesTags: (_result, _error, groupId) => [
        'device-actions',
        { type: 'group-actions', id: groupId },
      ],
    }),
    // GET {{BaseURL}}/api/commander/airconnect/status/workspace/:encodedWorkspaceID
    getAirConnectStatusByWorkspace: build.query<
      DeviceStatusAirConnectAPIResponse[] | undefined,
      string
    >({
      query: (workspaceId: string) => {
        return {
          url: `${backendDevice}/airconnect/status/workspace/${toBase64(
            workspaceId
          )}`,
          method: 'get',
        };
      },
      providesTags: ['device-actions'],
    }),

    // POST {{BaseURL}}/api/commander/airconnect/trigger/group/:encodedGroupID
    syncAirConnectStatusByGroup: build.mutation<void, string>({
      query: (groupId: string) => {
        return {
          url: `${backendDevice}/airconnect/trigger/group/${toBase64(groupId)}`,
          method: 'post',
        };
      },
      invalidatesTags: (_result, _error, groupId) => [
        { type: 'group-actions', id: groupId },
      ],
    }),
  }),
});
